import { format, isSameMonth, isSameYear, isThisYear } from 'date-fns';

import { DateFormat, formatDate, toDate } from './formatDate';
import { DateLike } from './types';

export enum RangeFormat {
  /**
   * 5:30 - 10:30 AM
   *
   * 5:30 AM - 10:30 PM
   */
  Time = 'Time',

  /**
   * Mon - Tue
   */
  Day = 'Day',

  /**
   * 16 - 25 Aug
   *
   * 16 Jul - 25 Aug
   *
   * 16 - 25 Aug 2021
   *
   * 16 Jul - 25 Aug 2021
   *
   * 16 Jul 2020 - 25 Aug 2021
   */
  Date = 'Date',

  /**
   * Jul - Aug
   *
   * Jul - Aug 2021
   *
   * Jul 2020 - Aug 2021
   */
  Month = 'Month',
}

interface Options {
  includeYear?: boolean;
  separator?: string;
}

export const formatRange = (
  startDate: DateLike,
  endDate: DateLike,
  formatEnum: RangeFormat,
  options: Options = { includeYear: false },
) => {
  startDate = toDate(startDate);
  endDate = toDate(endDate);
  let formattedStartDate = '';
  let formattedEndDate = '';
  const shouldIncludeYear = options.includeYear || !isThisYear(startDate);

  switch (formatEnum) {
    case RangeFormat.Time: {
      if (format(startDate, ' a') === format(endDate, ' a')) {
        formattedStartDate = format(startDate, 'h:mm');
      } else {
        formattedStartDate = formatDate(startDate, DateFormat.Time, options);
      }
      formattedEndDate = formatDate(endDate, DateFormat.Time);
      break;
    }
    case RangeFormat.Day: {
      formattedStartDate = formatDate(startDate, DateFormat.Day);
      formattedEndDate = formatDate(endDate, DateFormat.Day);
      break;
    }
    case RangeFormat.Date: {
      if (isSameMonth(startDate, endDate)) {
        formattedStartDate = format(startDate, 'd');
      } else if (isSameYear(startDate, endDate)) {
        formattedStartDate = format(startDate, 'd MMM');
      } else {
        formattedStartDate = formatDate(startDate, DateFormat.Date, { includeYear: true });
      }
      formattedEndDate = formatDate(endDate, DateFormat.Date, { includeYear: shouldIncludeYear });
      break;
    }
    case RangeFormat.Month: {
      formattedStartDate = format(startDate, 'MMM yyyy');
      formattedEndDate = format(endDate, 'MMM yyyy');
      break;
    }
    default:
      throw new Error(`Invalid format enum value: ${formatEnum}`);
  }

  return `${formattedStartDate} ${options.separator ?? '-'} ${formattedEndDate}`;
};

export const formatRangeWithYear = (
  startDate: DateLike,
  endDate: DateLike,
  formatEnum: RangeFormat,
  options: Omit<Options, 'includeYear'> = {},
) => {
  return formatRange(startDate, endDate, formatEnum, { ...options, includeYear: true });
};
